import { useState, useEffect, useRef, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  ArrowLeft, Monitor, MonitorOff, Play, Pause, SkipBack, Volume2,
  Image, FileText, Youtube, BarChart3, Type, QrCode, Clock,
  Moon, Trash2, Plus, ChevronLeft, ChevronRight, Send, Tv,
  Wifi, WifiOff, GripVertical, Copy, ExternalLink, Loader2,
  Upload, X, Film, Radio, Zap, Square, Maximize, Settings,
  Printer, ToggleLeft, ToggleRight, Keyboard, Timer, Palette, Hourglass,
  Video, Users, Camera, Mic, MicOff, AlertCircle, RefreshCw, LayoutGrid, Move,
  Circle, StopCircle, HardDrive, Download, Usb
} from "lucide-react";
import useCameraManager from "@/hooks/useCameraManager";
import { useBroadcastMaster } from "@/hooks/useBroadcastSync";
import SceneComposer from "@/components/SceneComposer";
import PtzControls from "@/components/PtzControls";
import DevicePanel from "@/components/DevicePanel";
import useRecorder from "@/hooks/useRecorder";
import useDeviceManager from "@/hooks/useDeviceManager";

const API = process.env.REACT_APP_BACKEND_URL;
const WS_URL = API.replace("https://", "wss://").replace("http://", "ws://");

const SOURCE_ICONS = {
  livestats: BarChart3, image: Image, pdf: FileText, youtube: Youtube,
  video_url: Film, local_video: Film, text: Type, qr: QrCode, wait: Clock, black: Moon, countdown: Hourglass,
  livekit_speakers: Video, camera: Camera, scene: LayoutGrid, ip_camera: Radio,
};

const SOURCE_LABELS = {
  livestats: "Live Stats", image: "Image", pdf: "PDF", youtube: "YouTube",
  video_url: "Video URL", local_video: "Video locale", text: "Texte", qr: "QR Code", wait: "Attente", black: "Ecran noir", countdown: "Compte a rebours",
  livekit_speakers: "Intervenants Live", camera: "Camera", scene: "Scene", ip_camera: "Camera IP",
};

function getSourceThumb(src) {
  if (src.type === "image" && src.config?.url) return src.config.url;
  if (src.type === "pdf" && src.config?.pages?.[0]?.thumb_url) return src.config.pages[0].thumb_url;
  if (src.type === "youtube" && src.config?.video_id) return `https://img.youtube.com/vi/${src.config.video_id}/default.jpg`;
  return null;
}

export default function LiveControlPage() {
  const { token, user } = useAuth();
  const { eventId, orgId } = useParams();
  const navigate = useNavigate();
  const headers = { Authorization: `Bearer ${token}` };

  const [event, setEvent] = useState(null);
  const [session, setSession] = useState(null);
  const [sources, setSources] = useState([]);
  const [scenes, setScenes] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  // Preview / Program
  const [preview, setPreview] = useState({ type: "black" });
  const [program, setProgram] = useState({ type: "black" });
  const [transition, setTransition] = useState("cut");
  const [sending, setSending] = useState(false);

  // Display status
  const [displayCount, setDisplayCount] = useState(0);
  const [wsConnected, setWsConnected] = useState(false);

  // Modals
  const [showAddSource, setShowAddSource] = useState(false);
  const [addSourceType, setAddSourceType] = useState(null);
  const [showAddScene, setShowAddScene] = useState(false);

  // PDF viewer
  const [pdfSource, setPdfSource] = useState(null);
  const [selectedPdfPage, setSelectedPdfPage] = useState(1);

  // YouTube form
  const [ytUrl, setYtUrl] = useState("");
  const [ytName, setYtName] = useState("");

  // Text form
  const [textContent, setTextContent] = useState("");
  const [textName, setTextName] = useState("");

  // Scene form
  const [sceneName, setSceneName] = useState("");

  // Direct mode (skip preview, go live immediately)
  const [directMode, setDirectMode] = useState(false);

  // Drag & drop scenes
  const [draggedSceneIdx, setDraggedSceneIdx] = useState(null);

  // Scene timer
  const [programStartTime, setProgramStartTime] = useState(null);
  const [timerElapsed, setTimerElapsed] = useState(0);

  // Keyboard shortcuts modal
  const [showShortcuts, setShowShortcuts] = useState(false);

  // Multi-screen guide
  const [showMultiScreenGuide, setShowMultiScreenGuide] = useState(false);

  // Wait screen theme form
  const [waitMessage, setWaitMessage] = useState("L'evenement va commencer dans quelques instants");
  const [waitBgColor, setWaitBgColor] = useState("#0B1120");
  const [waitBgEnd, setWaitBgEnd] = useState("#1a1a2e");
  const [waitLogoUrl, setWaitLogoUrl] = useState("");
  const waitLogoInputRef = useRef(null);

  // Countdown form
  const [countdownDate, setCountdownDate] = useState("");
  const [countdownTime, setCountdownTime] = useState("");
  const [countdownMsg, setCountdownMsg] = useState("L'evenement commence dans");
  const [countdownEndMsg, setCountdownEndMsg] = useState("C'est parti !");

  // Video upload
  const [videoUploading, setVideoUploading] = useState(false);
  const [videoUploadProgress, setVideoUploadProgress] = useState(0);
  const videoInputRef = useRef(null);

  // Camera management
  const cam = useCameraManager(eventId);
  const [camSelectedDevice, setCamSelectedDevice] = useState("");
  const [camName, setCamName] = useState("");
  const [camAudioEnabled, setCamAudioEnabled] = useState(false);
  const [camPreviewStream, setCamPreviewStream] = useState(null);
  const [camEnumerating, setCamEnumerating] = useState(false);
  const [camError, setCamError] = useState("");
  const camPreviewVideoRef = useRef(null);

  // Scene composition
  const [showSceneComposer, setShowSceneComposer] = useState(false);

  // IP Camera
  const [ipCamUrl, setIpCamUrl] = useState("");
  const [ipCamName, setIpCamName] = useState("");
  const [ipCamFormat, setIpCamFormat] = useState("auto");
  const [ipCamTesting, setIpCamTesting] = useState(false);
  const [ipCamError, setIpCamError] = useState("");
  const [ipCamOk, setIpCamOk] = useState(false);
  const [ipCamPtzUrl, setIpCamPtzUrl] = useState("");
  const [ipCamPtzProtocol, setIpCamPtzProtocol] = useState("generic_cgi");

  // PTZ panel
  const [showPtzPanel, setShowPtzPanel] = useState(false);
  const [ptzSource, setPtzSource] = useState(null);

  // Recording
  const recorder = useRecorder();
  const programMonitorRef = useRef(null);

  // Device Manager
  const deviceMgr = useDeviceManager();
  const [showDevicePanel, setShowDevicePanel] = useState(false);

  const wsRef = useRef(null);
  const fileInputRef = useRef(null);
  const pdfInputRef = useRef(null);

  // ===== BROADCAST SYNC (Detachable Windows) =====
  const { broadcastState, onCommand, detachedWindows } = useBroadcastMaster(session?.id);

  // ===== INIT =====
  const init = useCallback(async () => {
    try {
      const evRes = await axios.get(`${API}/api/event/events/${eventId}`, { headers });
      setEvent(evRes.data);

      // Create or get session
      let sess;
      try {
        const sessRes = await axios.get(`${API}/api/broadcast/sessions/${eventId}`, { headers });
        sess = sessRes.data;
      } catch {
        const newSess = await axios.post(`${API}/api/broadcast/sessions`, { event_id: eventId }, { headers });
        sess = newSess.data;
      }
      setSession(sess);
      setProgram(sess.current_program || { type: "black" });

      // Load sources, scenes, history
      const [srcRes, scnRes, histRes] = await Promise.all([
        axios.get(`${API}/api/broadcast/sources/${sess.id}`, { headers }),
        axios.get(`${API}/api/broadcast/scenes/${sess.id}`, { headers }),
        axios.get(`${API}/api/broadcast/history/${sess.id}?limit=20`, { headers }),
      ]);
      setSources(srcRes.data);
      setScenes(scnRes.data);
      setHistory(histRes.data);
    } catch (e) {
      toast.error("Erreur initialisation regie");
    } finally { setLoading(false); }
  }, [eventId, token]);

  useEffect(() => { init(); }, [init]);

  // ===== WEBSOCKET =====
  useEffect(() => {
    if (!session) return;
    const ws = new WebSocket(`${WS_URL}/api/broadcast/ws/control/${session.id}`);
    wsRef.current = ws;
    ws.onopen = () => {
      setWsConnected(true);
      const hb = setInterval(() => { if (ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ type: "ping" })); }, 15000);
      ws._hb = hb;
    };
    ws.onmessage = (evt) => {
      try {
        const msg = JSON.parse(evt.data);
        if (msg.type === "screen_status") setDisplayCount(msg.connected_displays);
        if (msg.type === "program_state") setProgram(msg.data || { type: "black" });
      } catch {}
    };
    ws.onclose = () => { clearInterval(ws._hb); setWsConnected(false); };
    ws.onerror = () => ws.close();
    return () => { clearInterval(ws._hb); ws.close(); };
  }, [session]);

  // ===== KEYBOARD SHORTCUTS =====
  useEffect(() => {
    const handler = (e) => {
      if (e.target.tagName === "INPUT" || e.target.tagName === "TEXTAREA" || e.target.tagName === "SELECT") return;

      // F1-F9: Load scene N and GO LIVE immediately
      const fMatch = e.key.match(/^F(\d)$/);
      if (fMatch) {
        const idx = parseInt(fMatch[1]) - 1;
        if (idx >= 0 && idx < scenes.length) {
          e.preventDefault();
          const scene = scenes[idx];
          const content = scene.source_config || { type: "black" };
          setPreview(content);
          // Go live immediately with this scene
          if (wsRef.current?.readyState === WebSocket.OPEN) {
            wsRef.current.send(JSON.stringify({ type: "set_program", content, transition }));
          }
          setProgram({ ...content });
          toast.success(`Scene "${scene.name}" en direct`, { duration: 1500 });
          return;
        }
      }

      // Ctrl+1-9: Load scene N into PREVIEW only
      if (e.ctrlKey && !e.shiftKey && !e.altKey) {
        const num = parseInt(e.key);
        if (num >= 1 && num <= scenes.length) {
          e.preventDefault();
          loadScene(scenes[num - 1]);
          toast.info(`Scene "${scenes[num - 1].name}" en preview`, { duration: 1500 });
          return;
        }
      }

      if (e.key === "Enter") { e.preventDefault(); goLive(); }
      if (e.key === "Escape") { e.preventDefault(); goBlack(); }
      if (e.key === " " && !e.ctrlKey) { e.preventDefault(); goLive(); } // Space = go live
      if (e.key === "?" || (e.shiftKey && e.key === "/")) { e.preventDefault(); setShowShortcuts(v => !v); }
      if (e.key === "d" || e.key === "D") { e.preventDefault(); setDirectMode(v => !v); }
      if (e.key === "r" || e.key === "R") {
        e.preventDefault();
        if (recorder.recording) { recorder.stopRecording(); toast.success("Enregistrement arrete"); }
        else {
          const el = programMonitorRef.current;
          recorder.startRecording(el, true).then(ok => { if (ok) toast.success("Enregistrement demarre"); });
        }
      }

      // Number keys 1-9 for sources (no modifier)
      if (!e.ctrlKey && !e.altKey && !e.shiftKey) {
        const num = parseInt(e.key);
        if (num >= 1 && num <= sources.length) {
          e.preventDefault();
          selectSourceForPreview(sources[num - 1]);
        }
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [sources, scenes, preview, transition, directMode]);

  // ===== SCENE TIMER =====
  useEffect(() => {
    setProgramStartTime(Date.now());
    setTimerElapsed(0);
  }, [program.type, program.name, program.config?.page_num, program.config?.source_id, program.config?.video_id]);

  useEffect(() => {
    if (!programStartTime || program.type === "black") { setTimerElapsed(0); return; }
    const iv = setInterval(() => setTimerElapsed(Math.floor((Date.now() - programStartTime) / 1000)), 1000);
    return () => clearInterval(iv);
  }, [programStartTime, program.type]);

  // ===== BROADCAST STATE TO DETACHED WINDOWS =====
  useEffect(() => {
    broadcastState({
      preview, program, sources, scenes,
      wsConnected, displayCount, timerElapsed,
    });
  }, [preview, program, sources, scenes, wsConnected, displayCount, timerElapsed, broadcastState]);

  // ===== LISTEN FOR COMMANDS FROM DETACHED WINDOWS =====
  useEffect(() => {
    return onCommand((cmd) => {
      if (cmd.action === "go_live") goLive();
      if (cmd.action === "go_black") goBlack();
      if (cmd.action === "select_source" && typeof cmd.sourceIndex === "number" && sources[cmd.sourceIndex]) {
        selectSourceForPreview(sources[cmd.sourceIndex]);
      }
      if (cmd.action === "load_scene" && typeof cmd.sceneIndex === "number" && scenes[cmd.sceneIndex]) {
        loadScene(scenes[cmd.sceneIndex]);
      }
    });
  }, [onCommand, sources, scenes]);

  // ===== ACTIONS =====
  const goLive = async () => {
    if (!session || sending) return;
    setSending(true);
    try {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ type: "set_program", content: preview, transition }));
      } else {
        await axios.post(`${API}/api/broadcast/program/${session.id}`, { content: preview, transition }, { headers });
      }
      setProgram({ ...preview });
      // Refresh history
      const histRes = await axios.get(`${API}/api/broadcast/history/${session.id}?limit=20`, { headers });
      setHistory(histRes.data);
    } catch { toast.error("Erreur de diffusion"); }
    finally { setSending(false); }
  };

  const goBlack = () => {
    setPreview({ type: "black" });
    if (session && wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: "set_program", content: { type: "black" }, transition: "cut" }));
      setProgram({ type: "black" });
    }
  };

  const goWait = () => {
    const content = { type: "wait", message: "L'evenement va commencer dans quelques instants" };
    setPreview(content);
  };

  // ===== DETACHABLE WINDOWS =====
  const openDetachedWindow = (panelType) => {
    if (!session) return;
    const url = `/detached-panel/${session.id}?panel=${panelType}`;
    const sizes = {
      preview: "width=960,height=640",
      program: "width=960,height=640",
      sources: "width=320,height=700",
    };
    window.open(url, `glotech_${panelType}_${session.id}`, `${sizes[panelType] || "width=800,height=600"},menubar=no,toolbar=no,location=no,status=no`);
    toast.success(`Panneau "${panelType}" detache`, { duration: 2000 });
  };

  const sendDirectLive = useCallback((content) => {
    if (!session || !wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) return;
    wsRef.current.send(JSON.stringify({ type: "set_program", content, transition }));
    setProgram({ ...content });
  }, [session, transition]);

  const selectSourceForPreview = async (source) => {
    let content = null;
    if (source.type === "pdf") {
      setPdfSource(source);
      setSelectedPdfPage(source.config?.current_page || 1);
      const page = source.config?.pages?.[0];
      if (page) content = { type: "pdf", name: `${source.name} - Page 1`, config: { full_url: page.full_url, page_num: 1, source_id: source.id } };
    } else if (source.type === "image") {
      content = { type: "image", name: source.name, config: source.config };
    } else if (source.type === "youtube") {
      content = { type: "youtube", name: source.name, config: { ...source.config, autoplay: directMode } };
    } else if (source.type === "video_url") {
      content = { type: "video_url", name: source.name, config: { ...source.config, autoplay: directMode } };
    } else if (source.type === "livestats") {
      content = { type: "livestats", name: "Live Stats", config: { event_id: eventId } };
    } else if (source.type === "text") {
      content = { type: "text", name: source.name, config: source.config };
    } else if (source.type === "qr") {
      content = { type: "qr", name: source.name, config: source.config };
    } else if (source.type === "wait") {
      content = { type: "wait", name: "Ecran d'attente", message: source.config?.message, config: source.config };
    } else if (source.type === "black") {
      content = { type: "black", name: "Ecran noir" };
    } else if (source.type === "countdown") {
      content = { type: "countdown", name: source.name, config: source.config };
    } else if (source.type === "local_video") {
      content = { type: "local_video", name: source.name, config: { ...source.config, autoplay: directMode } };
    } else if (source.type === "livekit_speakers") {
      content = { type: "livekit_speakers", name: source.name, config: source.config };
    } else if (source.type === "camera") {
      const cameraSourceId = source._cameraSourceId || source.config?.source_id;
      // Ensure camera is started
      if (!cam.cameraStreams[cameraSourceId]) {
        const result = await cam.startCamera(cameraSourceId, source.config?.device_id, source.config?.audio_enabled);
        if (!result.ok) { toast.error(result.message); return; }
      }
      content = { type: "camera", name: source.name, config: { ...source.config, source_id: cameraSourceId } };
    } else if (source.type === "scene") {
      content = { type: "scene", name: source.name, config: source.config };
    } else if (source.type === "ip_camera") {
      content = { type: "ip_camera", name: source.name, config: source.config };
    }
    if (content) {
      setPreview(content);
      if (directMode) sendDirectLive(content);
    }
  };

  const selectPdfPage = (pageNum) => {
    if (!pdfSource) return;
    const page = pdfSource.config?.pages?.[pageNum - 1];
    if (page) {
      setSelectedPdfPage(pageNum);
      const content = { type: "pdf", name: `${pdfSource.name} - Page ${pageNum}`, config: { full_url: page.full_url, page_num: pageNum, source_id: pdfSource.id } };
      setPreview(content);
      if (directMode) sendDirectLive(content);
    }
  };

  // ===== ADD SOURCES =====
  const addLiveStats = async () => {
    const res = await axios.post(`${API}/api/broadcast/sources`, {
      session_id: session.id, type: "livestats", name: "Live Stats", config: { event_id: eventId },
    }, { headers });
    setSources(prev => [...prev, res.data]);
    setShowAddSource(false);
    toast.success("Source Live Stats ajoutee");
  };

  const addBlackScreen = async () => {
    const res = await axios.post(`${API}/api/broadcast/sources`, {
      session_id: session.id, type: "black", name: "Ecran noir", config: {},
    }, { headers });
    setSources(prev => [...prev, res.data]);
    toast.success("Ecran noir ajoute");
  };

  const addLiveKitSpeakers = async () => {
    const roomName = `event-${eventId}`;
    try {
      const tokenRes = await axios.post(`${API}/api/livekit/viewer-token`, { room_name: roomName }, { headers });
      const { token: viewerToken, livekit_url } = tokenRes.data;
      const res = await axios.post(`${API}/api/broadcast/sources`, {
        session_id: session.id, type: "livekit_speakers", name: "Intervenants Live",
        config: { room_name: roomName, viewer_token: viewerToken, livekit_url, event_id: eventId },
      }, { headers });
      setSources(prev => [...prev, res.data]);
      setShowAddSource(false);
      toast.success("Source Intervenants Live ajoutee");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur LiveKit");
    }
  };

  // ===== CAMERA SOURCE MANAGEMENT =====
  const initCameraModal = async () => {
    setAddSourceType("camera");
    setCamError("");
    setCamSelectedDevice("");
    setCamName("");
    setCamAudioEnabled(false);
    setCamPreviewStream(null);
    setCamEnumerating(true);
    const result = await cam.enumerateDevices();
    setCamEnumerating(false);
    if (!result.ok) setCamError(result.message);
  };

  const previewCameraDevice = async (deviceId) => {
    setCamSelectedDevice(deviceId);
    setCamError("");
    // Stop previous preview
    if (camPreviewStream) camPreviewStream.getTracks().forEach(t => t.stop());
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { deviceId: { exact: deviceId }, width: { ideal: 640 }, height: { ideal: 360 } },
        audio: false,
      });
      setCamPreviewStream(stream);
      const device = cam.devices.find(d => d.deviceId === deviceId);
      if (!camName) setCamName(device?.label || `Camera ${sources.filter(s => s.type === "camera").length + 1}`);
    } catch (e) {
      setCamError(`Impossible d'acceder a ce peripherique: ${e.message}`);
    }
  };

  // Attach modal preview stream to video ref
  useEffect(() => {
    if (camPreviewVideoRef.current && camPreviewStream) {
      camPreviewVideoRef.current.srcObject = camPreviewStream;
    }
    return () => {
      if (camPreviewVideoRef.current) camPreviewVideoRef.current.srcObject = null;
    };
  }, [camPreviewStream]);

  const addCameraSource = async () => {
    if (!camSelectedDevice || !session) return;
    // Stop modal preview
    if (camPreviewStream) camPreviewStream.getTracks().forEach(t => t.stop());
    setCamPreviewStream(null);

    const sourceId = `cam-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const device = cam.devices.find(d => d.deviceId === camSelectedDevice);
    const name = camName.trim() || device?.label || "Camera";

    // Start the actual camera capture
    const result = await cam.startCamera(sourceId, camSelectedDevice, camAudioEnabled);
    if (!result.ok) { toast.error(result.message); return; }

    // Save metadata to backend
    try {
      const res = await axios.post(`${API}/api/broadcast/sources`, {
        session_id: session.id, type: "camera", name,
        config: { device_id: camSelectedDevice, device_label: device?.label || "", source_id: sourceId, audio_enabled: camAudioEnabled },
      }, { headers });
      setSources(prev => [...prev, { ...res.data, _cameraSourceId: sourceId }]);
      setShowAddSource(false);
      setAddSourceType(null);
      toast.success(`Camera "${name}" ajoutee`);
    } catch (e) {
      cam.stopCamera(sourceId);
      toast.error("Erreur lors de l'ajout de la source camera");
    }
  };

  const deleteCameraSource = (source) => {
    const cameraSourceId = source._cameraSourceId || source.config?.source_id;
    if (cameraSourceId) cam.stopCamera(cameraSourceId);
  };

  // Handle program relay: when camera goes to PROGRAM, start BroadcastChannel frame capture
  useEffect(() => {
    if (program.type === "camera") {
      const cameraSourceId = program.config?.source_id;
      if (cameraSourceId && cam.cameraStreams[cameraSourceId]) {
        cam.startRelay(cameraSourceId);
      }
    } else {
      cam.stopRelay();
    }
  }, [program.type, program.config?.source_id]);

  // Cleanup camera on source deletion
  const deleteSource = async (sourceId) => {
    const source = sources.find(s => s.id === sourceId);
    if (source?.type === "camera") deleteCameraSource(source);
    try {
      await axios.delete(`${API}/api/broadcast/sources/${sourceId}`, { headers });
      setSources(prev => prev.filter(s => s.id !== sourceId));
      toast.success("Source supprimee");
    } catch { toast.error("Erreur suppression"); }
  };

  // ===== SCENE COMPOSITION =====
  const handleSceneReady = async (sceneConfig) => {
    if (!session) return;
    try {
      const res = await axios.post(`${API}/api/broadcast/sources`, {
        session_id: session.id,
        type: "scene",
        name: sceneConfig.name,
        config: sceneConfig.config,
      }, { headers });
      setSources(prev => [...prev, res.data]);
      setShowSceneComposer(false);
      setShowAddSource(false);
      toast.success(`Scene "${sceneConfig.name}" ajoutee`);
    } catch (e) {
      toast.error("Erreur lors de la creation de la scene");
    }
  };

  const addWaitScreen = async () => {
    // Open the wait theme form in the add source modal
    setAddSourceType("wait");
  };

  const submitWaitScreen = async () => {
    const res = await axios.post(`${API}/api/broadcast/sources`, {
      session_id: session.id, type: "wait", name: "Ecran d'attente",
      config: { message: waitMessage, bg_color: waitBgColor, bg_end: waitBgEnd, logo_url: waitLogoUrl },
    }, { headers });
    setSources(prev => [...prev, res.data]);
    setShowAddSource(false);
    setAddSourceType(null);
    toast.success("Ecran d'attente ajoute");
  };

  const handleWaitLogoUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => setWaitLogoUrl(ev.target.result);
    reader.readAsDataURL(file);
  };

  const submitCountdown = async () => {
    if (!countdownDate || !countdownTime) { toast.error("Date et heure requises"); return; }
    const targetTime = new Date(`${countdownDate}T${countdownTime}`).toISOString();
    const res = await axios.post(`${API}/api/broadcast/sources`, {
      session_id: session.id, type: "countdown", name: `Compte a rebours`,
      config: { target_time: targetTime, message: countdownMsg, end_message: countdownEndMsg },
    }, { headers });
    setSources(prev => [...prev, res.data]);
    setShowAddSource(false);
    setAddSourceType(null);
    toast.success("Compte a rebours ajoute");
  };

  const uploadVideo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const allowed = [".mp4", ".webm", ".mov", ".avi", ".mkv"];
    const ext = file.name.substring(file.name.lastIndexOf(".")).toLowerCase();
    if (!allowed.includes(ext)) { toast.error("Format non supporte (MP4, WebM, MOV, AVI, MKV)"); return; }
    if (file.size > 200 * 1024 * 1024) { toast.error("Fichier trop volumineux (max 200 Mo)"); return; }

    setVideoUploading(true);
    setVideoUploadProgress(0);
    const formData = new FormData();
    formData.append("file", file);

    try {
      const res = await axios.post(`${API}/api/broadcast/upload-video?session_id=${session.id}`, formData, {
        headers: { ...headers, "Content-Type": "multipart/form-data" },
        onUploadProgress: (p) => { if (p.total) setVideoUploadProgress(Math.round((p.loaded / p.total) * 100)); },
      });
      setSources(prev => [...prev, res.data]);
      setShowAddSource(false);
      setAddSourceType(null);
      toast.success(`Video "${file.name}" ajoutee (${res.data.config?.size_mb} Mo)`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur upload video");
    } finally {
      setVideoUploading(false);
      setVideoUploadProgress(0);
      if (videoInputRef.current) videoInputRef.current.value = "";
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const formData = new FormData();
    formData.append("file", file);
    try {
      toast.loading("Upload en cours...");
      const res = await axios.post(`${API}/api/broadcast/upload-image?session_id=${session.id}`, formData, { headers: { ...headers, "Content-Type": "multipart/form-data" } });
      setSources(prev => [...prev, res.data]);
      setShowAddSource(false);
      toast.dismiss();
      toast.success(`Image "${file.name}" ajoutee`);
    } catch { toast.dismiss(); toast.error("Erreur upload image"); }
  };

  const handlePdfUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const formData = new FormData();
    formData.append("file", file);
    try {
      toast.loading("Traitement du PDF en cours...");
      const res = await axios.post(`${API}/api/broadcast/upload-pdf?session_id=${session.id}`, formData, { headers: { ...headers, "Content-Type": "multipart/form-data" } });
      setSources(prev => [...prev, res.data]);
      setShowAddSource(false);
      toast.dismiss();
      toast.success(`PDF "${file.name}" (${res.data.config.total_pages} pages) ajoute`);
    } catch (err) { toast.dismiss(); toast.error(err.response?.data?.detail || "Erreur upload PDF"); }
  };

  const addYouTube = async () => {
    if (!ytUrl) return;
    const match = ytUrl.match(/(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|watch\?v=|shorts\/))([a-zA-Z0-9_-]{11})/);
    if (!match) { toast.error("URL YouTube invalide"); return; }
    const videoId = match[1];
    const res = await axios.post(`${API}/api/broadcast/sources`, {
      session_id: session.id, type: "youtube", name: ytName || `YouTube ${videoId}`,
      config: { youtube_url: ytUrl, video_id: videoId, autoplay: true },
    }, { headers });
    setSources(prev => [...prev, res.data]);
    setYtUrl(""); setYtName("");
    setShowAddSource(false);
    toast.success("Source YouTube ajoutee");
  };

  const addText = async () => {
    if (!textContent) return;
    const res = await axios.post(`${API}/api/broadcast/sources`, {
      session_id: session.id, type: "text", name: textName || "Texte",
      config: { text: textContent, font_size: 48, color: "#ffffff", bg_color: "#0B1120", bold: false },
    }, { headers });
    setSources(prev => [...prev, res.data]);
    setTextContent(""); setTextName("");
    setShowAddSource(false);
    toast.success("Source Texte ajoutee");
  };

  // ===== IP CAMERA / NETWORK STREAM =====
  const detectStreamFormat = (url) => {
    if (!url) return "unknown";
    const lower = url.toLowerCase();
    if (lower.includes(".m3u8")) return "hls";
    if (lower.includes(".mpd")) return "dash";
    if (lower.endsWith(".mp4") || lower.endsWith(".webm")) return "direct";
    if (lower.startsWith("rtsp://") || lower.startsWith("rtmp://")) return "unsupported_native";
    if (lower.includes("whep") || lower.includes("webrtc")) return "webrtc";
    return "direct"; // try direct playback as default
  };

  const testIpCameraUrl = async () => {
    if (!ipCamUrl.trim()) return;
    setIpCamTesting(true);
    setIpCamError("");
    setIpCamOk(false);
    const format = ipCamFormat === "auto" ? detectStreamFormat(ipCamUrl) : ipCamFormat;

    if (format === "unsupported_native") {
      setIpCamError("Les flux RTSP/RTMP ne sont pas lisibles directement par le navigateur. Utilisez une passerelle locale (MediaMTX, FFmpeg) pour convertir vers HLS ou WebRTC.");
      setIpCamTesting(false);
      return;
    }

    try {
      // Quick HEAD/GET check for reachability
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 8000);
      await fetch(ipCamUrl, { method: "HEAD", mode: "no-cors", signal: controller.signal });
      clearTimeout(timeout);
      setIpCamOk(true);
    } catch (e) {
      // no-cors may fail on HEAD but still be playable
      setIpCamOk(true);
    }
    setIpCamTesting(false);
  };

  const addIpCamera = async () => {
    if (!ipCamUrl.trim() || !session) return;
    const format = ipCamFormat === "auto" ? detectStreamFormat(ipCamUrl) : ipCamFormat;
    const name = ipCamName.trim() || "Camera IP";
    try {
      const config = { url: ipCamUrl.trim(), format, name };
      if (ipCamPtzUrl.trim()) {
        config.ptz_url = ipCamPtzUrl.trim();
        config.ptz_protocol = ipCamPtzProtocol;
        config.ptz_enabled = true;
      }
      const res = await axios.post(`${API}/api/broadcast/sources`, {
        session_id: session.id, type: "ip_camera", name, config,
      }, { headers });
      setSources(prev => [...prev, res.data]);
      setShowAddSource(false);
      setAddSourceType(null);
      setIpCamUrl("");
      setIpCamName("");
      setIpCamOk(false);
      setIpCamPtzUrl("");
      toast.success(`Camera IP "${name}" ajoutee`);
    } catch (e) {
      toast.error("Erreur lors de l'ajout");
    }
  };
  const addScene = async () => {
    if (!sceneName) return;
    const res = await axios.post(`${API}/api/broadcast/scenes`, {
      session_id: session.id, name: sceneName, source_type: preview.type, source_config: preview,
    }, { headers });
    setScenes(prev => [...prev, res.data]);
    setSceneName("");
    setShowAddScene(false);
    toast.success("Scene ajoutee");
  };

  const deleteScene = async (sceneId) => {
    await axios.delete(`${API}/api/broadcast/scenes/${sceneId}`, { headers });
    setScenes(prev => prev.filter(s => s.id !== sceneId));
  };

  const loadScene = (scene) => {
    const content = scene.source_config || { type: "black" };
    setPreview(content);
    if (scene.source_type === "pdf" && scene.source_config?.config?.source_id) {
      const src = sources.find(s => s.id === scene.source_config.config.source_id);
      if (src) { setPdfSource(src); setSelectedPdfPage(scene.source_config.config?.page_num || 1); }
    }
    if (directMode) sendDirectLive(content);
  };

  // ===== DRAG & DROP SCENES =====
  const handleDragStart = (idx) => setDraggedSceneIdx(idx);
  const handleDragOver = (e) => e.preventDefault();
  const handleDrop = async (dropIdx) => {
    if (draggedSceneIdx === null || draggedSceneIdx === dropIdx) { setDraggedSceneIdx(null); return; }
    const reordered = [...scenes];
    const [moved] = reordered.splice(draggedSceneIdx, 1);
    reordered.splice(dropIdx, 0, moved);
    setScenes(reordered);
    setDraggedSceneIdx(null);
    try {
      await axios.put(`${API}/api/broadcast/scenes-reorder/${session.id}`, { scene_ids: reordered.map(s => s.id) }, { headers });
    } catch { toast.error("Erreur de reordonnancement"); }
  };

  // ===== EXPORT CONDUCTEUR =====
  const exportConducteur = () => {
    const printWin = window.open("", "_blank");
    if (!printWin) { toast.error("Popup bloquee, autorisez les popups"); return; }
    const rows = scenes.map((s, i) => `
      <tr>
        <td style="padding:8px 12px;border-bottom:1px solid #e2e8f0;text-align:center;font-weight:600;color:#64748b">${String(i + 1).padStart(2, "0")}</td>
        <td style="padding:8px 12px;border-bottom:1px solid #e2e8f0;font-weight:500">${s.name}</td>
        <td style="padding:8px 12px;border-bottom:1px solid #e2e8f0;color:#64748b">${SOURCE_LABELS[s.source_type] || s.source_type}</td>
        <td style="padding:8px 12px;border-bottom:1px solid #e2e8f0;color:#94a3b8;font-size:12px">${s.source_config?.name || ""}</td>
      </tr>`).join("");
    printWin.document.write(`<!DOCTYPE html><html><head><title>Conducteur — ${event?.title || "Evenement"}</title>
      <style>
        body{font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;margin:40px;color:#1e293b}
        h1{font-size:22px;margin-bottom:4px}
        .meta{color:#64748b;font-size:13px;margin-bottom:24px}
        table{width:100%;border-collapse:collapse;border:1px solid #e2e8f0;border-radius:8px;overflow:hidden}
        th{background:#f8fafc;padding:10px 12px;text-align:left;font-size:12px;text-transform:uppercase;letter-spacing:0.05em;color:#64748b;border-bottom:2px solid #e2e8f0}
        @media print{body{margin:20px}button{display:none!important}}
      </style></head><body>
      <h1>Conducteur — ${event?.title || "Evenement"}</h1>
      <p class="meta">${new Date().toLocaleDateString("fr-FR", { weekday: "long", year: "numeric", month: "long", day: "numeric" })} — ${scenes.length} scene${scenes.length > 1 ? "s" : ""}</p>
      <table>
        <thead><tr><th style="width:50px">#</th><th>Scene</th><th>Type</th><th>Detail</th></tr></thead>
        <tbody>${rows || '<tr><td colspan="4" style="padding:20px;text-align:center;color:#94a3b8">Aucune scene configuree</td></tr>'}</tbody>
      </table>
      <br/><button onclick="window.print()" style="padding:8px 20px;background:#0099CC;color:white;border:none;border-radius:6px;cursor:pointer;font-size:14px">Imprimer</button>
    </body></html>`);
    printWin.document.close();
  };

  // ===== VIDEO COMMANDS =====
  const sendVideoCommand = (command, value) => {
    if (!session) return;
    axios.post(`${API}/api/broadcast/video-command/${session.id}`, { command, value }, { headers });
  };

  // ===== BROADCAST URL =====
  const broadcastUrl = session ? `${API.replace("/api", "")}/broadcast/${session.id}?key=${session.broadcast_key}` : "";

  const copyBroadcastUrl = () => {
    navigator.clipboard.writeText(broadcastUrl);
    toast.success("URL de diffusion copiee");
  };

  const openBroadcast = () => window.open(broadcastUrl, "_blank");

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <Loader2 className="w-8 h-8 animate-spin text-[#0099CC]" />
    </div>
  );

  const backUrl = orgId ? `/app/event-center/org/${orgId}/event/${eventId}` : `/app/evenements/${eventId}`;

  return (
    <div className="h-[calc(100vh-64px)] flex flex-col overflow-hidden" data-testid="live-control-page">
      {/* Top Bar */}
      <div className="bg-[#060B14] border-b border-[#1A2332] px-4 py-2 flex items-center justify-between flex-shrink-0">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate(backUrl)} className="text-[#94A3B8] hover:text-white h-8 w-8">
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <Radio className="w-4 h-4 text-red-500" />
          <span className="text-white font-bold text-sm">GLOTECH LIVE CONTROL</span>
          <span className="text-[#64748B] text-xs">— {event?.title}</span>
        </div>
        <div className="flex items-center gap-3">
          {/* Connection status */}
          <div className="flex items-center gap-1.5">
            {wsConnected ? <Wifi className="w-3.5 h-3.5 text-green-400" /> : <WifiOff className="w-3.5 h-3.5 text-red-400" />}
            <span className="text-xs text-[#64748B]">{wsConnected ? "Connecte" : "Deconnecte"}</span>
          </div>
          <div className="flex items-center gap-1.5 bg-[#0B1120] px-2.5 py-1 rounded-lg border border-[#1A2332]">
            {displayCount > 0 ? <Tv className="w-3.5 h-3.5 text-green-400" /> : <MonitorOff className="w-3.5 h-3.5 text-[#64748B]" />}
            <span className="text-xs text-white">{displayCount} ecran{displayCount !== 1 ? "s" : ""}</span>
          </div>
          <Button size="sm" variant="outline" onClick={copyBroadcastUrl} className="border-[#1A2332] text-[#94A3B8] h-7 text-xs" data-testid="copy-broadcast-url">
            <Copy className="w-3 h-3 mr-1" /> URL diffusion
          </Button>
          <Button size="sm" variant="outline" onClick={exportConducteur} className="border-[#1A2332] text-[#94A3B8] h-7 text-xs" data-testid="export-conducteur-btn">
            <Printer className="w-3 h-3 mr-1" /> Conducteur
          </Button>
          <Button size="sm" variant="outline" onClick={() => setShowShortcuts(true)} className="border-[#1A2332] text-[#94A3B8] h-7 text-xs" data-testid="shortcuts-btn">
            <Keyboard className="w-3 h-3 mr-1" /> ?
          </Button>
          <Button size="sm" variant="outline" onClick={() => setShowMultiScreenGuide(true)} className="border-[#1A2332] text-[#94A3B8] h-7 text-xs" data-testid="multiscreen-guide-btn">
            <Monitor className="w-3 h-3 mr-1" /> Multi-ecran
          </Button>
          <Button size="sm" variant="outline" onClick={() => { setShowDevicePanel(true); deviceMgr.scan(true); }} className="border-[#1A2332] text-[#94A3B8] h-7 text-xs" data-testid="devices-btn">
            <Usb className="w-3 h-3 mr-1" /> Peripheriques
            {deviceMgr.captureCards.length > 0 && (
              <span className="ml-1 w-4 h-4 rounded-full bg-[#F97316] text-black text-[8px] font-bold flex items-center justify-center">{deviceMgr.captureCards.length}</span>
            )}
          </Button>
          <Button size="sm" onClick={openBroadcast} className="bg-[#0099CC] hover:bg-[#0088B5] text-white h-7 text-xs" data-testid="open-broadcast-btn">
            <ExternalLink className="w-3 h-3 mr-1" /> Ouvrir ecran
          </Button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left: Sources + Scenes */}
        <div className="w-64 bg-[#060B14] border-r border-[#1A2332] flex flex-col overflow-hidden flex-shrink-0">
          {/* Sources */}
          <div className="flex-1 overflow-y-auto p-3 space-y-1">
            <div className="flex items-center justify-between mb-2">
              <span className="text-[10px] text-[#64748B] uppercase tracking-widest font-bold">Sources</span>
              <div className="flex items-center gap-1.5">
                <button onClick={() => openDetachedWindow("sources")} className="text-[#64748B] hover:text-[#0099CC] transition-colors" title="Detacher Sources & Scenes" data-testid="detach-sources-btn">
                  <ExternalLink className="w-3.5 h-3.5" />
                </button>
                <button onClick={() => { setShowAddSource(true); setAddSourceType(null); }} className="text-[#0099CC] hover:text-white" data-testid="add-source-btn">
                  <Plus className="w-4 h-4" />
                </button>
              </div>
            </div>
            {sources.map((src, i) => {
              const Icon = SOURCE_ICONS[src.type] || Image;
              const isPreview = preview.type === src.type && (src.type !== "pdf" || preview.config?.source_id === src.id);
              const isProgram = program.type === src.type && (src.type !== "pdf" || program.config?.source_id === src.id);
              const thumb = getSourceThumb(src);
              return (
                <div
                  key={src.id}
                  onClick={() => selectSourceForPreview(src)}
                  className={`flex items-center gap-2 px-2 py-1.5 rounded-lg cursor-pointer group transition-all text-xs ${
                    isPreview ? "bg-[#0099CC]/20 border border-[#0099CC]/40" :
                    isProgram ? "bg-red-500/10 border border-red-500/30" :
                    "hover:bg-[#0B1120] border border-transparent"
                  }`}
                  data-testid={`source-${i}`}
                >
                  <span className="text-[#64748B] w-4 text-center font-mono">{i + 1}</span>
                  {thumb ? (
                    <img src={thumb} alt="" className="w-8 h-5 rounded object-cover flex-shrink-0 bg-white/5" />
                  ) : (
                    <Icon className={`w-3.5 h-3.5 flex-shrink-0 ${isProgram ? "text-red-400" : isPreview ? "text-[#0099CC]" : "text-[#64748B]"}`} />
                  )}
                  <span className={`truncate flex-1 ${isProgram ? "text-red-400" : isPreview ? "text-[#0099CC]" : "text-[#94A3B8]"}`}>
                    {src.name}
                  </span>
                  {isProgram && <span className="text-[8px] bg-red-500 text-white px-1 rounded font-bold">LIVE</span>}
                  {src.type === "ip_camera" && src.config?.ptz_enabled && (
                    <button
                      onClick={(e) => { e.stopPropagation(); setPtzSource(src); setShowPtzPanel(true); }}
                      className="opacity-0 group-hover:opacity-100 text-[#F97316] hover:text-[#EA580C] p-0.5 transition-opacity"
                      title="Pilotage PTZ"
                      data-testid={`ptz-btn-${i}`}
                    >
                      <Move className="w-2.5 h-2.5" />
                    </button>
                  )}
                  {src.type === "camera" && (
                    <span className="flex items-center gap-0.5">
                      {cam.cameraStreams[src._cameraSourceId || src.config?.source_id]
                        ? <span className="w-1.5 h-1.5 rounded-full bg-[#10B981] animate-pulse" title="Camera active" />
                        : <span className="w-1.5 h-1.5 rounded-full bg-[#64748b]" title="Camera arretee" />
                      }
                      <button onClick={(e) => { e.stopPropagation(); cam.toggleAudio(src._cameraSourceId || src.config?.source_id); }}
                        className="opacity-0 group-hover:opacity-100 transition-opacity p-0.5" title="Toggle audio">
                        {cam.cameraStreams[src._cameraSourceId || src.config?.source_id]?.audioEnabled
                          ? <Mic className="w-2.5 h-2.5 text-[#10B981]" />
                          : <MicOff className="w-2.5 h-2.5 text-[#64748b]" />
                        }
                      </button>
                    </span>
                  )}
                  <button onClick={(e) => { e.stopPropagation(); deleteSource(src.id); }} className="opacity-0 group-hover:opacity-100 text-red-400 hover:text-red-300">
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              );
            })}
          </div>

          {/* Scenes */}
          <div className="border-t border-[#1A2332] p-3 max-h-[35%] overflow-y-auto">
            <div className="flex items-center justify-between mb-2">
              <span className="text-[10px] text-[#64748B] uppercase tracking-widest font-bold">Scenes</span>
              <button onClick={() => setShowAddScene(true)} className="text-[#0099CC] hover:text-white" data-testid="add-scene-btn">
                <Plus className="w-4 h-4" />
              </button>
            </div>
            {scenes.map((scene, i) => (
              <div
                key={scene.id}
                draggable
                onDragStart={() => handleDragStart(i)}
                onDragOver={handleDragOver}
                onDrop={() => handleDrop(i)}
                className={`flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-[#0B1120] cursor-pointer group text-xs transition-all ${draggedSceneIdx === i ? "opacity-40" : ""}`}
                onClick={() => loadScene(scene)}
                data-testid={`scene-${i}`}
              >
                <GripVertical className="w-3 h-3 text-[#3A4452] flex-shrink-0 cursor-grab" />
                {i < 9 && (
                  <kbd className="text-[8px] font-mono bg-[#1A2332] text-[#F97316] px-1 py-0.5 rounded border border-[#2A3441] flex-shrink-0" title={`F${i + 1} = Go Live`}>F{i + 1}</kbd>
                )}
                <span className="text-[#94A3B8] truncate flex-1">{scene.name}</span>
                <button onClick={(e) => { e.stopPropagation(); deleteScene(scene.id); }} className="opacity-0 group-hover:opacity-100 text-red-400">
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Center: Preview + Program */}
        <div className="flex-1 flex flex-col overflow-hidden bg-[#080E1A]">
          <div className="flex-1 flex gap-3 p-3 min-h-0">
            {/* PREVIEW */}
            <div className="flex-1 flex flex-col min-w-0">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-2 h-2 rounded-full bg-[#0099CC]" />
                <span className="text-[10px] text-[#0099CC] uppercase tracking-widest font-bold">Preview</span>
                <button
                  onClick={() => openDetachedWindow("preview")}
                  className="ml-auto text-[#64748B] hover:text-[#0099CC] transition-colors"
                  title="Detacher en fenetre separee"
                  data-testid="detach-preview-btn"
                >
                  <ExternalLink className="w-3.5 h-3.5" />
                </button>
                {detachedWindows.has("preview") && <span className="text-[8px] bg-[#0099CC]/20 text-[#0099CC] px-1.5 py-0.5 rounded font-bold">DETACHE</span>}
              </div>
              <div className="flex-1 bg-black rounded-xl overflow-hidden border-2 border-[#0099CC]/30 relative" data-testid="preview-monitor">
                <ContentRenderer content={preview} isPreview cameraStreams={cam.cameraStreams} sources={sources} />
                {preview.name && (
                  <div className="absolute bottom-0 left-0 right-0 bg-black/70 px-3 py-1.5">
                    <p className="text-white text-xs truncate">{preview.name || preview.type}</p>
                  </div>
                )}
              </div>
            </div>

            {/* PROGRAM */}
            <div className="flex-1 flex flex-col min-w-0">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
                <span className="text-[10px] text-red-400 uppercase tracking-widest font-bold">Program — En direct</span>
                {program.type !== "black" && timerElapsed > 0 && (
                  <span className="ml-auto flex items-center gap-1 text-[11px] font-mono tabular-nums text-red-400/80 bg-red-500/10 px-2 py-0.5 rounded" data-testid="scene-timer">
                    <Timer className="w-3 h-3" />
                    {String(Math.floor(timerElapsed / 60)).padStart(2, "0")}:{String(timerElapsed % 60).padStart(2, "0")}
                  </span>
                )}
                <button
                  onClick={() => openDetachedWindow("program")}
                  className={`${program.type !== "black" && timerElapsed > 0 ? "" : "ml-auto"} text-[#64748B] hover:text-red-400 transition-colors`}
                  title="Detacher en fenetre separee"
                  data-testid="detach-program-btn"
                >
                  <ExternalLink className="w-3.5 h-3.5" />
                </button>
                {detachedWindows.has("program") && <span className="text-[8px] bg-red-500/20 text-red-400 px-1.5 py-0.5 rounded font-bold">DETACHE</span>}
              </div>
              <div ref={programMonitorRef} className="flex-1 bg-black rounded-xl overflow-hidden border-2 border-red-500/30 relative" data-testid="program-monitor">
                <ContentRenderer content={program} cameraStreams={cam.cameraStreams} sources={sources} />
                {program.name && (
                  <div className="absolute bottom-0 left-0 right-0 bg-black/70 px-3 py-1.5">
                    <p className="text-white text-xs truncate">{program.name || program.type}</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* PDF Page Grid (shown when PDF source selected) */}
          {pdfSource && (
            <div className="flex-shrink-0 border-t border-[#1A2332] p-3 max-h-[30%] overflow-y-auto bg-[#060B14]" data-testid="pdf-pages-grid">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[10px] text-[#64748B] uppercase tracking-widest font-bold">{pdfSource.name} — {pdfSource.config?.total_pages} pages</span>
                <div className="flex items-center gap-2">
                  <Button size="sm" variant="outline" onClick={() => selectPdfPage(Math.max(1, selectedPdfPage - 1))} disabled={selectedPdfPage <= 1} className="h-6 w-6 p-0 border-[#1A2332] text-[#94A3B8]">
                    <ChevronLeft className="w-3 h-3" />
                  </Button>
                  <span className="text-xs text-white">Page {selectedPdfPage}/{pdfSource.config?.total_pages}</span>
                  <Button size="sm" variant="outline" onClick={() => selectPdfPage(Math.min(pdfSource.config?.total_pages || 1, selectedPdfPage + 1))} disabled={selectedPdfPage >= (pdfSource.config?.total_pages || 1)} className="h-6 w-6 p-0 border-[#1A2332] text-[#94A3B8]">
                    <ChevronRight className="w-3 h-3" />
                  </Button>
                </div>
              </div>
              <div className="flex gap-2 overflow-x-auto pb-2">
                {pdfSource.config?.pages?.map((page, i) => (
                  <div
                    key={i}
                    onClick={() => selectPdfPage(i + 1)}
                    className={`flex-shrink-0 w-24 cursor-pointer rounded-lg overflow-hidden border-2 transition-all ${
                      selectedPdfPage === i + 1 ? "border-[#0099CC] ring-1 ring-[#0099CC]/50" :
                      program.config?.page_num === i + 1 && program.config?.source_id === pdfSource.id ? "border-red-500" :
                      "border-[#1A2332] hover:border-[#2A3441]"
                    }`}
                    data-testid={`pdf-page-${i + 1}`}
                  >
                    <img src={page.thumb_url} alt={`Page ${i + 1}`} className="w-full aspect-[3/4] object-cover bg-white" />
                    <div className="bg-[#0B1120] px-1.5 py-0.5 text-center">
                      <span className="text-[9px] text-[#64748B]">{i + 1}</span>
                      {program.config?.page_num === i + 1 && program.config?.source_id === pdfSource.id && (
                        <span className="text-[7px] bg-red-500 text-white px-1 rounded ml-1">LIVE</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Control Bar */}
          <div className="flex-shrink-0 border-t border-[#1A2332] bg-[#060B14] px-4 py-2.5 flex items-center justify-between">
            <div className="flex items-center gap-2">
              {/* Direct mode toggle */}
              <button
                onClick={() => setDirectMode(!directMode)}
                className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg border text-xs font-medium transition-all ${
                  directMode
                    ? "bg-amber-500/20 border-amber-500/50 text-amber-400"
                    : "bg-[#0B1120] border-[#1A2332] text-[#64748B] hover:text-[#94A3B8]"
                }`}
                data-testid="direct-mode-toggle"
              >
                {directMode ? <ToggleRight className="w-3.5 h-3.5" /> : <ToggleLeft className="w-3.5 h-3.5" />}
                DIRECT
              </button>

              {/* Transition */}
              <div className="flex items-center gap-1 bg-[#0B1120] rounded-lg border border-[#1A2332] p-0.5">
                <button onClick={() => setTransition("cut")} className={`px-2.5 py-1 rounded text-xs font-medium transition-all ${transition === "cut" ? "bg-[#1A2332] text-white" : "text-[#64748B]"}`} data-testid="transition-cut">
                  CUT
                </button>
                <button onClick={() => setTransition("fade")} className={`px-2.5 py-1 rounded text-xs font-medium transition-all ${transition === "fade" ? "bg-[#1A2332] text-white" : "text-[#64748B]"}`} data-testid="transition-fade">
                  FADE
                </button>
              </div>

              {/* Video controls (shown when youtube/video in program) */}
              {(program.type === "youtube" || program.type === "video_url") && (
                <div className="flex items-center gap-1 ml-2">
                  <Button size="sm" variant="outline" onClick={() => sendVideoCommand("play")} className="h-7 w-7 p-0 border-[#1A2332] text-[#94A3B8]"><Play className="w-3 h-3" /></Button>
                  <Button size="sm" variant="outline" onClick={() => sendVideoCommand("pause")} className="h-7 w-7 p-0 border-[#1A2332] text-[#94A3B8]"><Pause className="w-3 h-3" /></Button>
                  <Button size="sm" variant="outline" onClick={() => sendVideoCommand("restart")} className="h-7 w-7 p-0 border-[#1A2332] text-[#94A3B8]"><SkipBack className="w-3 h-3" /></Button>
                </div>
              )}
            </div>

            <div className="flex items-center gap-2">
              {/* Recording controls */}
              {!recorder.recording ? (
                <div className="flex items-center gap-1">
                  <select
                    value={recorder.quality}
                    onChange={e => recorder.setQuality(e.target.value)}
                    className="bg-[#0B1120] border border-[#1A2332] text-[#94A3B8] rounded px-1.5 py-1 text-[10px] h-8"
                    data-testid="rec-quality-select"
                  >
                    <option value="720p">720p</option>
                    <option value="1080p">1080p</option>
                    <option value="1440p">1440p</option>
                    <option value="4k">4K</option>
                  </select>
                  <Button
                    size="sm"
                    onClick={async () => {
                      const el = programMonitorRef.current;
                      const ok = await recorder.startRecording(el, true);
                      if (ok) toast.success(recorder.mode === "filesystem" ? "Enregistrement sur disque demarre" : "Enregistrement en memoire demarre");
                    }}
                    className="bg-red-600/20 hover:bg-red-600/40 text-red-400 border border-red-600/30 h-8 text-xs"
                    data-testid="rec-start-btn"
                  >
                    <Circle className="w-3 h-3 mr-1 fill-red-500 text-red-500" /> REC
                  </Button>
                </div>
              ) : (
                <div className="flex items-center gap-1.5 bg-red-500/10 border border-red-500/30 rounded-lg px-2 py-1" data-testid="rec-active-panel">
                  <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
                  <span className="text-red-400 text-[10px] font-mono tabular-nums">{recorder.durationFormatted}</span>
                  <span className="text-[#64748B] text-[9px]">{recorder.fileSizeFormatted}</span>
                  {recorder.mode === "filesystem" && (
                    <span className="text-[#10B981] text-[8px] flex items-center gap-0.5"><HardDrive className="w-2.5 h-2.5" />{recorder.segmentCount}</span>
                  )}
                  {recorder.mode === "memory" && (
                    <span className="text-[#F59E0B] text-[8px] flex items-center gap-0.5"><Download className="w-2.5 h-2.5" />MEM</span>
                  )}
                  <Button size="sm" onClick={recorder.togglePause} className="h-6 w-6 p-0 bg-transparent hover:bg-white/10 text-white" data-testid="rec-pause-btn">
                    {recorder.paused ? <Play className="w-3 h-3" /> : <Pause className="w-3 h-3" />}
                  </Button>
                  <Button size="sm" onClick={() => { recorder.stopRecording(); toast.success(recorder.mode === "filesystem" ? `${recorder.segmentCount} segment(s) sauvegarde(s) sur le disque` : "Telechargement de l'enregistrement..."); }} className="h-6 w-6 p-0 bg-transparent hover:bg-red-500/30 text-red-400" data-testid="rec-stop-btn">
                    <StopCircle className="w-3 h-3" />
                  </Button>
                </div>
              )}
              {recorder.error && <span className="text-red-400 text-[9px] max-w-32 truncate">{recorder.error}</span>}

              {/* Quick buttons */}
              <Button size="sm" variant="outline" onClick={goWait} className="border-[#1A2332] text-[#F59E0B] hover:bg-[#F59E0B]/10 h-8 text-xs" data-testid="btn-wait">
                <Clock className="w-3 h-3 mr-1" /> ATTENTE
              </Button>
              <Button size="sm" onClick={goBlack} className="bg-[#1A2332] hover:bg-[#2A3441] text-white h-8 text-xs" data-testid="btn-black">
                <Moon className="w-3 h-3 mr-1" /> ECRAN NOIR
              </Button>

              {/* LIVE BUTTON */}
              <Button
                size="lg"
                onClick={goLive}
                disabled={sending}
                className="bg-red-600 hover:bg-red-700 text-white font-bold px-6 h-10 text-sm shadow-lg shadow-red-600/30"
                data-testid="btn-go-live"
              >
                {sending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Zap className="w-4 h-4 mr-2" />}
                DIFFUSER
              </Button>
            </div>
          </div>
        </div>

        {/* Right: History */}
        <div className="w-48 bg-[#060B14] border-l border-[#1A2332] flex flex-col overflow-hidden flex-shrink-0">
          <div className="p-3">
            <span className="text-[10px] text-[#64748B] uppercase tracking-widest font-bold">Historique</span>
          </div>
          <div className="flex-1 overflow-y-auto px-3 space-y-1">
            {history.map((h, i) => (
              <div key={h.id || i} className="text-[10px] py-1 border-b border-[#1A2332]/50">
                <span className="text-[#64748B]">{new Date(h.timestamp).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit", second: "2-digit" })}</span>
                <span className="text-[#94A3B8] ml-1.5">{h.content_summary || h.action}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ===== ADD SOURCE MODAL ===== */}
      {showAddSource && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70" data-testid="add-source-modal">
          <div className="bg-[#0B1120] border border-[#1A2332] rounded-2xl p-6 max-w-lg w-full mx-4 space-y-4 max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between">
              <h3 className="text-white font-bold text-lg">Ajouter une source</h3>
              <button onClick={() => { setShowAddSource(false); setAddSourceType(null); }} className="text-[#64748B] hover:text-white"><X className="w-5 h-5" /></button>
            </div>

            {!addSourceType ? (
              <div className="grid grid-cols-3 gap-3">
                {[
                  { type: "livestats", label: "Live Stats", icon: BarChart3, color: "#10B981" },
                  { type: "image", label: "Image", icon: Image, color: "#0099CC" },
                  { type: "pdf", label: "PDF", icon: FileText, color: "#8B5CF6" },
                  { type: "youtube", label: "YouTube", icon: Youtube, color: "#EF4444" },
                  { type: "video_url", label: "Video URL", icon: Film, color: "#F59E0B" },
                  { type: "text", label: "Texte", icon: Type, color: "#64748B" },
                  { type: "local_video", label: "Video locale", icon: Film, color: "#06B6D4" },
                  { type: "countdown", label: "Compte a rebours", icon: Hourglass, color: "#EC4899" },
                  { type: "wait", label: "Attente", icon: Clock, color: "#F59E0B" },
                  { type: "black", label: "Ecran noir", icon: Moon, color: "#64748B" },
                  { type: "livekit_speakers", label: "Intervenants Live", icon: Video, color: "#10B981" },
                  { type: "camera", label: "Camera / Entree video", icon: Camera, color: "#22D3EE" },
                  { type: "scene", label: "Scene / Composition", icon: LayoutGrid, color: "#EC4899" },
                  { type: "ip_camera", label: "Camera IP / Flux reseau", icon: Radio, color: "#F97316" },
                ].map(item => (
                  <button
                    key={item.type}
                    onClick={() => {
                      if (item.type === "livestats") { addLiveStats(); return; }
                      if (item.type === "black") { addBlackScreen(); setShowAddSource(false); return; }
                      if (item.type === "livekit_speakers") { addLiveKitSpeakers(); return; }
                      if (item.type === "camera") { initCameraModal(); return; }
                      if (item.type === "scene") { setShowSceneComposer(true); setShowAddSource(false); return; }
                      if (item.type === "ip_camera") { setAddSourceType("ip_camera"); setIpCamUrl(""); setIpCamName(""); setIpCamError(""); setIpCamOk(false); return; }
                      if (item.type === "wait") { addWaitScreen(); return; }
                      if (item.type === "countdown") { setAddSourceType("countdown"); return; }
                      if (item.type === "local_video") { setAddSourceType("local_video"); return; }
                      if (item.type === "image") { fileInputRef.current?.click(); return; }
                      if (item.type === "pdf") { pdfInputRef.current?.click(); return; }
                      setAddSourceType(item.type);
                    }}
                    className="flex flex-col items-center gap-2 p-4 rounded-xl bg-[#060B14] border border-[#1A2332] hover:border-[#2A3441] transition-all"
                    data-testid={`add-${item.type}`}
                  >
                    <item.icon className="w-6 h-6" style={{ color: item.color }} />
                    <span className="text-white text-xs">{item.label}</span>
                  </button>
                ))}
              </div>
            ) : addSourceType === "youtube" ? (
              <div className="space-y-3">
                <Input value={ytName} onChange={e => setYtName(e.target.value)} placeholder="Nom (ex: Hymne national)" className="bg-[#060B14] border-[#1A2332] text-white" data-testid="yt-name-input" />
                <Input value={ytUrl} onChange={e => setYtUrl(e.target.value)} placeholder="URL YouTube" className="bg-[#060B14] border-[#1A2332] text-white" data-testid="yt-url-input" />
                <Button onClick={addYouTube} className="w-full bg-red-600 hover:bg-red-700 text-white" data-testid="yt-add-btn">Ajouter YouTube</Button>
              </div>
            ) : addSourceType === "video_url" ? (
              <div className="space-y-3">
                <Input value={ytName} onChange={e => setYtName(e.target.value)} placeholder="Nom" className="bg-[#060B14] border-[#1A2332] text-white" />
                <Input value={ytUrl} onChange={e => setYtUrl(e.target.value)} placeholder="URL video (mp4, webm)" className="bg-[#060B14] border-[#1A2332] text-white" />
                <Button onClick={async () => {
                  if (!ytUrl) return;
                  const res = await axios.post(`${API}/api/broadcast/sources`, {
                    session_id: session.id, type: "video_url", name: ytName || "Video",
                    config: { url: ytUrl, autoplay: true },
                  }, { headers });
                  setSources(prev => [...prev, res.data]);
                  setYtUrl(""); setYtName(""); setShowAddSource(false);
                  toast.success("Source video ajoutee");
                }} className="w-full bg-[#F59E0B] hover:bg-[#D97706] text-black">Ajouter</Button>
              </div>
            ) : addSourceType === "text" ? (
              <div className="space-y-3">
                <Input value={textName} onChange={e => setTextName(e.target.value)} placeholder="Nom" className="bg-[#060B14] border-[#1A2332] text-white" />
                <textarea value={textContent} onChange={e => setTextContent(e.target.value)} placeholder="Texte a afficher..." rows={3} className="w-full bg-[#060B14] border border-[#1A2332] text-white rounded-lg p-3 text-sm" data-testid="text-input" />
                <Button onClick={addText} className="w-full bg-[#0099CC] hover:bg-[#0088B5] text-white">Ajouter</Button>
              </div>
            ) : addSourceType === "wait" ? (
              <div className="space-y-3" data-testid="wait-theme-form">
                <label className="text-xs text-[#94A3B8]">Message d'attente</label>
                <Input value={waitMessage} onChange={e => setWaitMessage(e.target.value)} placeholder="L'evenement va commencer..." className="bg-[#060B14] border-[#1A2332] text-white" data-testid="wait-message-input" />
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-[#94A3B8] block mb-1">Couleur debut</label>
                    <div className="flex items-center gap-2">
                      <input type="color" value={waitBgColor} onChange={e => setWaitBgColor(e.target.value)} className="w-8 h-8 rounded border border-[#1A2332] cursor-pointer bg-transparent" data-testid="wait-bg-color" />
                      <span className="text-xs text-[#64748B] font-mono">{waitBgColor}</span>
                    </div>
                  </div>
                  <div>
                    <label className="text-xs text-[#94A3B8] block mb-1">Couleur fin</label>
                    <div className="flex items-center gap-2">
                      <input type="color" value={waitBgEnd} onChange={e => setWaitBgEnd(e.target.value)} className="w-8 h-8 rounded border border-[#1A2332] cursor-pointer bg-transparent" data-testid="wait-bg-end" />
                      <span className="text-xs text-[#64748B] font-mono">{waitBgEnd}</span>
                    </div>
                  </div>
                </div>
                <div>
                  <label className="text-xs text-[#94A3B8] block mb-1">Logo / Image de marque</label>
                  {waitLogoUrl ? (
                    <div className="relative inline-block">
                      <img src={waitLogoUrl} alt="Logo" className="h-16 rounded-lg border border-[#1A2332]" />
                      <button onClick={() => setWaitLogoUrl("")} className="absolute -top-1.5 -right-1.5 bg-red-500 text-white rounded-full w-4 h-4 flex items-center justify-center"><X className="w-3 h-3" /></button>
                    </div>
                  ) : (
                    <button onClick={() => waitLogoInputRef.current?.click()} className="flex items-center gap-2 px-3 py-2 bg-[#060B14] border border-[#1A2332] rounded-lg text-[#94A3B8] text-xs hover:border-[#2A3441]" data-testid="wait-logo-upload">
                      <Upload className="w-3.5 h-3.5" /> Charger un logo
                    </button>
                  )}
                  <input ref={waitLogoInputRef} type="file" accept="image/*" className="hidden" onChange={handleWaitLogoUpload} />
                </div>
                <div className="p-3 rounded-lg border border-[#1A2332]" style={{ background: `linear-gradient(135deg, ${waitBgColor}, ${waitBgEnd})` }}>
                  <p className="text-[10px] text-[#64748B] mb-1">Apercu</p>
                  <div className="flex flex-col items-center gap-2 py-3">
                    {waitLogoUrl && <img src={waitLogoUrl} alt="" className="h-8 object-contain" />}
                    <p className="text-white/80 text-xs text-center">{waitMessage || "..."}</p>
                  </div>
                </div>
                <Button onClick={submitWaitScreen} className="w-full bg-[#F59E0B] hover:bg-[#D97706] text-black font-medium" data-testid="wait-submit-btn">
                  <Palette className="w-3.5 h-3.5 mr-1" /> Ajouter ecran d'attente
                </Button>
              </div>
            ) : addSourceType === "countdown" ? (
              <div className="space-y-3" data-testid="countdown-form">
                <label className="text-xs text-[#94A3B8]">Message avant le debut</label>
                <Input value={countdownMsg} onChange={e => setCountdownMsg(e.target.value)} placeholder="L'evenement commence dans" className="bg-[#060B14] border-[#1A2332] text-white" data-testid="countdown-msg-input" />
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-[#94A3B8] block mb-1">Date cible</label>
                    <Input type="date" value={countdownDate} onChange={e => setCountdownDate(e.target.value)} className="bg-[#060B14] border-[#1A2332] text-white" data-testid="countdown-date" />
                  </div>
                  <div>
                    <label className="text-xs text-[#94A3B8] block mb-1">Heure cible</label>
                    <Input type="time" value={countdownTime} onChange={e => setCountdownTime(e.target.value)} className="bg-[#060B14] border-[#1A2332] text-white" data-testid="countdown-time" />
                  </div>
                </div>
                <label className="text-xs text-[#94A3B8]">Message quand termine</label>
                <Input value={countdownEndMsg} onChange={e => setCountdownEndMsg(e.target.value)} placeholder="C'est parti !" className="bg-[#060B14] border-[#1A2332] text-white" data-testid="countdown-end-msg" />
                <Button onClick={submitCountdown} className="w-full bg-[#EC4899] hover:bg-[#DB2777] text-white font-medium" data-testid="countdown-submit-btn">
                  <Hourglass className="w-3.5 h-3.5 mr-1" /> Ajouter le compte a rebours
                </Button>
              </div>
            ) : addSourceType === "local_video" ? (
              <div className="space-y-4" data-testid="video-upload-form">
                <div className="text-center p-6 border-2 border-dashed border-[#1A2332] rounded-xl hover:border-[#0099CC]/50 transition-colors">
                  <Film className="w-10 h-10 text-[#06B6D4] mx-auto mb-3" />
                  <p className="text-white text-sm font-medium mb-1">Charger une video</p>
                  <p className="text-[#64748B] text-xs mb-3">MP4, WebM, MOV, AVI, MKV — Max 200 Mo</p>
                  {videoUploading ? (
                    <div className="space-y-2">
                      <div className="w-full bg-[#1A2332] rounded-full h-2">
                        <div className="bg-[#06B6D4] h-2 rounded-full transition-all duration-300" style={{ width: `${videoUploadProgress}%` }} />
                      </div>
                      <p className="text-[#06B6D4] text-xs font-mono">{videoUploadProgress}% — Upload en cours...</p>
                    </div>
                  ) : (
                    <Button onClick={() => videoInputRef.current?.click()} className="bg-[#06B6D4] hover:bg-[#0891B2] text-white" data-testid="video-select-btn">
                      <Upload className="w-3.5 h-3.5 mr-1" /> Choisir un fichier
                    </Button>
                  )}
                  <input ref={videoInputRef} type="file" accept="video/mp4,video/webm,video/quicktime,video/x-msvideo,video/x-matroska,.mp4,.webm,.mov,.avi,.mkv" className="hidden" onChange={uploadVideo} data-testid="video-file-input" />
                </div>
              </div>
            ) : addSourceType === "camera" ? (
              <div className="space-y-3" data-testid="camera-picker-form">
                {camEnumerating ? (
                  <div className="flex flex-col items-center gap-3 py-6">
                    <RefreshCw className="w-6 h-6 text-[#22D3EE] animate-spin" />
                    <p className="text-white text-sm">Detection des peripheriques video...</p>
                  </div>
                ) : camError ? (
                  <div className="flex flex-col items-center gap-3 py-4">
                    <AlertCircle className="w-8 h-8 text-red-400" />
                    <p className="text-red-400 text-xs text-center">{camError}</p>
                    <Button onClick={initCameraModal} variant="outline" className="border-[#2A3441] text-white" data-testid="camera-retry-btn">
                      <RefreshCw className="w-3.5 h-3.5 mr-1" /> Reessayer
                    </Button>
                  </div>
                ) : (
                  <>
                    <label className="text-xs text-[#94A3B8]">Peripherique video detecte</label>
                    <div className="space-y-1.5 max-h-32 overflow-y-auto">
                      {cam.devices.length === 0 ? (
                        <p className="text-[#64748b] text-xs text-center py-3">Aucun peripherique detecte</p>
                      ) : cam.devices.map((d, i) => (
                        <button
                          key={d.deviceId}
                          onClick={() => previewCameraDevice(d.deviceId)}
                          className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg border text-left text-xs transition-all ${
                            camSelectedDevice === d.deviceId
                              ? "border-[#22D3EE] bg-[#22D3EE]/10 text-[#22D3EE]"
                              : "border-[#1A2332] text-[#94A3B8] hover:border-[#2A3441]"
                          }`}
                          data-testid={`camera-device-${i}`}
                        >
                          <Camera className="w-3.5 h-3.5 shrink-0" />
                          <span className="truncate">{d.label || `Camera ${i + 1}`}</span>
                        </button>
                      ))}
                    </div>

                    {/* Camera preview */}
                    {camPreviewStream && (
                      <div className="relative rounded-lg overflow-hidden border border-[#22D3EE]/30 bg-black aspect-video">
                        <video
                          ref={camPreviewVideoRef}
                          autoPlay
                          playsInline
                          muted
                          className="w-full h-full object-contain"
                          data-testid="camera-modal-preview"
                        />
                        <div className="absolute top-1 left-1 bg-[#22D3EE] text-black text-[8px] font-bold px-1.5 py-0.5 rounded">APERCU</div>
                      </div>
                    )}

                    {/* Name */}
                    <div>
                      <label className="text-xs text-[#94A3B8] block mb-1">Nom de la source</label>
                      <Input
                        value={camName}
                        onChange={e => setCamName(e.target.value)}
                        placeholder="Camera 1, Camera intervenant..."
                        className="bg-[#060B14] border-[#1A2332] text-white"
                        data-testid="camera-name-input"
                      />
                    </div>

                    {/* Audio toggle */}
                    <div className="flex items-center justify-between">
                      <label className="text-xs text-[#94A3B8] flex items-center gap-1.5">
                        {camAudioEnabled ? <Mic className="w-3 h-3 text-[#10B981]" /> : <MicOff className="w-3 h-3 text-[#64748b]" />}
                        Audio de la camera
                      </label>
                      <button
                        onClick={() => setCamAudioEnabled(v => !v)}
                        className={`w-9 h-5 rounded-full transition-colors relative ${camAudioEnabled ? "bg-[#10B981]" : "bg-[#2A3441]"}`}
                        data-testid="camera-audio-toggle"
                      >
                        <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform ${camAudioEnabled ? "translate-x-4" : "translate-x-0.5"}`} />
                      </button>
                    </div>

                    {/* Info note */}
                    <div className="bg-[#0B1120] border border-[#1A2332] rounded-lg p-2.5">
                      <p className="text-[10px] text-[#64748b] leading-relaxed">
                        Compatible : webcams USB, cartes d'acquisition HDMI (Elgato, BlackMagic, AVerMedia), cameras professionnelles via capture card.
                        L'audio est desactive en PREVIEW pour eviter les echos — il sera actif en PROGRAM.
                      </p>
                    </div>

                    <Button
                      onClick={addCameraSource}
                      disabled={!camSelectedDevice}
                      className="w-full bg-[#22D3EE] hover:bg-[#06B6D4] text-black font-medium disabled:opacity-40"
                      data-testid="camera-add-btn"
                    >
                      <Camera className="w-3.5 h-3.5 mr-1" /> Ajouter cette camera
                    </Button>
                  </>
                )}
              </div>
            ) : addSourceType === "ip_camera" ? (
              <div className="space-y-3" data-testid="ip-camera-form">
                <div>
                  <label className="text-xs text-[#94A3B8] block mb-1">URL du flux video</label>
                  <Input
                    value={ipCamUrl}
                    onChange={e => { setIpCamUrl(e.target.value); setIpCamOk(false); setIpCamError(""); }}
                    placeholder="https://... (.m3u8, .mp4, WebRTC)"
                    className="bg-[#060B14] border-[#1A2332] text-white font-mono text-xs"
                    data-testid="ip-cam-url-input"
                  />
                  {ipCamUrl && (
                    <div className="mt-1 flex items-center gap-1.5">
                      <span className="text-[9px] text-[#64748b]">Format detecte:</span>
                      <span className="text-[9px] font-semibold text-[#F97316]">{detectStreamFormat(ipCamUrl).toUpperCase()}</span>
                    </div>
                  )}
                </div>
                <div>
                  <label className="text-xs text-[#94A3B8] block mb-1">Nom de la source</label>
                  <Input
                    value={ipCamName}
                    onChange={e => setIpCamName(e.target.value)}
                    placeholder="Camera IP 1, Canon CR-N300..."
                    className="bg-[#060B14] border-[#1A2332] text-white"
                    data-testid="ip-cam-name-input"
                  />
                </div>
                <div>
                  <label className="text-xs text-[#94A3B8] block mb-1">Format du flux</label>
                  <select value={ipCamFormat} onChange={e => setIpCamFormat(e.target.value)}
                    className="w-full bg-[#060B14] border border-[#1A2332] text-white rounded-lg px-3 py-1.5 text-xs"
                    data-testid="ip-cam-format-select">
                    <option value="auto">Auto-detection</option>
                    <option value="hls">HLS (.m3u8)</option>
                    <option value="direct">Video directe (MP4/WebM)</option>
                    <option value="webrtc">WebRTC (WHEP)</option>
                  </select>
                </div>

                {ipCamError && (
                  <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-2.5">
                    <p className="text-red-400 text-[10px]"><AlertCircle className="w-3 h-3 inline mr-1" />{ipCamError}</p>
                  </div>
                )}
                {ipCamOk && (
                  <div className="bg-[#10B981]/10 border border-[#10B981]/30 rounded-lg p-2.5">
                    <p className="text-[#10B981] text-[10px]">URL accessible — pret a ajouter</p>
                  </div>
                )}

                <div className="bg-[#0B1120] border border-[#1A2332] rounded-lg p-2.5">
                  <p className="text-[10px] text-[#64748b] leading-relaxed">
                    Compatible : flux HLS (.m3u8), MP4/WebM directs, WebRTC (WHEP).
                    Pour les cameras RTSP/ONVIF, utilisez une passerelle MediaMTX ou FFmpeg pour convertir vers HLS/WebRTC.
                  </p>
                </div>

                {/* PTZ Configuration (optional) */}
                <div className="border border-[#1A2332] rounded-lg p-2.5 bg-[#080E1A]">
                  <div className="flex items-center gap-1.5 mb-2">
                    <Move className="w-3 h-3 text-[#F97316]" />
                    <span className="text-[10px] text-[#F97316] font-bold uppercase">Pilotage PTZ (optionnel)</span>
                  </div>
                  <div className="space-y-2">
                    <Input
                      value={ipCamPtzUrl}
                      onChange={e => setIpCamPtzUrl(e.target.value)}
                      placeholder="http://192.168.1.100 (URL de controle PTZ)"
                      className="bg-[#060B14] border-[#1A2332] text-white font-mono text-xs"
                      data-testid="ip-cam-ptz-url"
                    />
                    {ipCamPtzUrl && (
                      <select
                        value={ipCamPtzProtocol}
                        onChange={e => setIpCamPtzProtocol(e.target.value)}
                        className="w-full bg-[#060B14] border border-[#1A2332] text-white rounded-lg px-2 py-1.5 text-xs"
                        data-testid="ip-cam-ptz-protocol"
                      >
                        <option value="canon_cgi">Canon (CR-N300/XU-81)</option>
                        <option value="onvif_http">ONVIF / Standard HTTP</option>
                        <option value="ptzoptics">PTZ Optics</option>
                        <option value="generic_cgi">Generique (CGI)</option>
                      </select>
                    )}
                  </div>
                </div>

                <div className="flex gap-2">
                  <Button onClick={testIpCameraUrl} disabled={!ipCamUrl.trim() || ipCamTesting}
                    variant="outline" className="flex-1 border-[#1A2332] text-[#94A3B8]" data-testid="ip-cam-test-btn">
                    {ipCamTesting ? <RefreshCw className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Wifi className="w-3.5 h-3.5 mr-1" />}
                    Tester l'URL
                  </Button>
                  <Button onClick={addIpCamera} disabled={!ipCamUrl.trim()}
                    className="flex-1 bg-[#F97316] hover:bg-[#EA580C] text-white" data-testid="ip-cam-add-btn">
                    <Radio className="w-3.5 h-3.5 mr-1" /> Ajouter
                  </Button>
                </div>
              </div>
            ) : null}

            {/* Hidden file inputs */}
            <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleImageUpload} />
            <input ref={pdfInputRef} type="file" accept=".pdf" className="hidden" onChange={handlePdfUpload} />
          </div>
        </div>
      )}

      {/* ===== SCENE COMPOSER ===== */}
      {showSceneComposer && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm" onClick={() => setShowSceneComposer(false)}>
          <div className="max-w-lg w-full mx-4" onClick={e => e.stopPropagation()}>
            <SceneComposer
              sources={sources}
              cameraStreams={cam.cameraStreams}
              onSceneReady={handleSceneReady}
              onCancel={() => setShowSceneComposer(false)}
            />
          </div>
        </div>
      )}

      {/* ===== KEYBOARD SHORTCUTS MODAL ===== */}
      {showShortcuts && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70" onClick={() => setShowShortcuts(false)} data-testid="shortcuts-modal">
          <div className="bg-[#0B1120] border border-[#1A2332] rounded-2xl p-6 max-w-md w-full mx-4" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-white font-bold flex items-center gap-2"><Keyboard className="w-5 h-5 text-[#0099CC]" /> Raccourcis clavier</h3>
              <button onClick={() => setShowShortcuts(false)} className="text-[#64748B] hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <div className="space-y-2">
              {[
                { keys: "1 - 9", desc: "Selectionner la source N en preview" },
                { keys: "F1 - F9", desc: "Charger la scene N et diffuser en direct" },
                { keys: "Ctrl + 1-9", desc: "Charger la scene N en preview" },
                { keys: "Entree / Espace", desc: "Diffuser (passer en LIVE)" },
                { keys: "Echap", desc: "Ecran noir immediat" },
                { keys: "D", desc: "Basculer le mode Direct" },
                { keys: "R", desc: "Demarrer / arreter l'enregistrement" },
                { keys: "?", desc: "Ouvrir / fermer ce panneau" },
              ].map((s, i) => (
                <div key={i} className="flex items-center justify-between py-2 px-3 rounded-lg bg-[#060B14] border border-[#1A2332]">
                  <span className="text-[#94A3B8] text-sm">{s.desc}</span>
                  <kbd className="bg-[#1A2332] text-white text-xs font-mono px-2.5 py-1 rounded-md border border-[#2A3441]">{s.keys}</kbd>
                </div>
              ))}
            </div>
            <p className="text-[10px] text-[#64748B] mt-4 text-center">Les raccourcis sont desactives quand un champ texte est actif.</p>
          </div>
        </div>
      )}

      {/* ===== MULTI-SCREEN GUIDE ===== */}
      {showMultiScreenGuide && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm" onClick={() => setShowMultiScreenGuide(false)} data-testid="multiscreen-guide-modal">
          <div className="bg-[#0B1120] border border-[#1A2332] rounded-2xl p-6 max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-white font-bold text-lg flex items-center gap-2"><Monitor className="w-5 h-5 text-[#0099CC]" /> Guide Multi-Ecran</h3>
              <button onClick={() => setShowMultiScreenGuide(false)} className="text-[#64748B] hover:text-white"><X className="w-5 h-5" /></button>
            </div>

            <div className="space-y-4">
              {/* Step 1 */}
              <div className="bg-[#060B14] rounded-xl p-4 border border-[#1A2332]">
                <div className="flex items-center gap-2 mb-2">
                  <span className="w-6 h-6 rounded-full bg-[#0099CC] text-white text-xs font-bold flex items-center justify-center">1</span>
                  <h4 className="text-white font-semibold text-sm">Detacher un panneau</h4>
                </div>
                <p className="text-[#94A3B8] text-xs leading-relaxed">
                  Cliquez sur l'icone <ExternalLink className="w-3 h-3 inline text-[#0099CC]" /> en haut de chaque panneau (Preview, Program, Sources) 
                  pour l'ouvrir dans une fenetre independante.
                </p>
              </div>

              {/* Step 2 */}
              <div className="bg-[#060B14] rounded-xl p-4 border border-[#1A2332]">
                <div className="flex items-center gap-2 mb-2">
                  <span className="w-6 h-6 rounded-full bg-[#0099CC] text-white text-xs font-bold flex items-center justify-center">2</span>
                  <h4 className="text-white font-semibold text-sm">Placer sur vos ecrans</h4>
                </div>
                <p className="text-[#94A3B8] text-xs leading-relaxed">
                  Glissez chaque fenetre detachee sur un ecran different. Configuration recommandee :
                </p>
                <div className="mt-2 grid grid-cols-3 gap-2">
                  <div className="bg-[#0B1120] rounded-lg p-2 text-center border border-[#1A2332]">
                    <Monitor className="w-4 h-4 text-[#64748B] mx-auto mb-1" />
                    <p className="text-[9px] text-[#0099CC] font-bold">Ecran 1</p>
                    <p className="text-[8px] text-[#64748B]">Regie principale</p>
                  </div>
                  <div className="bg-[#0B1120] rounded-lg p-2 text-center border border-[#1A2332]">
                    <Monitor className="w-4 h-4 text-[#64748B] mx-auto mb-1" />
                    <p className="text-[9px] text-red-400 font-bold">Ecran 2</p>
                    <p className="text-[8px] text-[#64748B]">Program (LIVE)</p>
                  </div>
                  <div className="bg-[#0B1120] rounded-lg p-2 text-center border border-[#1A2332]">
                    <Monitor className="w-4 h-4 text-[#64748B] mx-auto mb-1" />
                    <p className="text-[9px] text-green-400 font-bold">Ecran 3</p>
                    <p className="text-[8px] text-[#64748B]">Preview</p>
                  </div>
                </div>
              </div>

              {/* Step 3 */}
              <div className="bg-[#060B14] rounded-xl p-4 border border-[#1A2332]">
                <div className="flex items-center gap-2 mb-2">
                  <span className="w-6 h-6 rounded-full bg-[#0099CC] text-white text-xs font-bold flex items-center justify-center">3</span>
                  <h4 className="text-white font-semibold text-sm">Synchronisation automatique</h4>
                </div>
                <p className="text-[#94A3B8] text-xs leading-relaxed">
                  Toutes les fenetres sont synchronisees en temps reel. Les sources selectionnees, les scenes chargees, 
                  et l'etat du programme se mettent a jour instantanement partout.
                </p>
              </div>

              {/* Step 4 */}
              <div className="bg-[#060B14] rounded-xl p-4 border border-[#1A2332]">
                <div className="flex items-center gap-2 mb-2">
                  <span className="w-6 h-6 rounded-full bg-amber-500 text-black text-xs font-bold flex items-center justify-center">!</span>
                  <h4 className="text-white font-semibold text-sm">A savoir</h4>
                </div>
                <ul className="text-[#94A3B8] text-xs leading-relaxed space-y-1.5">
                  <li className="flex items-start gap-1.5">
                    <Camera className="w-3 h-3 text-[#22D3EE] mt-0.5 flex-shrink-0" />
                    <span>Les flux camera USB et LiveKit restent sur la fenetre principale (pas de duplication materielle)</span>
                  </li>
                  <li className="flex items-start gap-1.5">
                    <Radio className="w-3 h-3 text-[#F97316] mt-0.5 flex-shrink-0" />
                    <span>Les cameras IP (HLS/WebRTC) fonctionnent dans toutes les fenetres</span>
                  </li>
                  <li className="flex items-start gap-1.5">
                    <Keyboard className="w-3 h-3 text-[#0099CC] mt-0.5 flex-shrink-0" />
                    <span>Les raccourcis clavier (F1-F9, Espace, etc.) fonctionnent depuis la regie principale</span>
                  </li>
                </ul>
              </div>
            </div>

            <div className="mt-5 flex gap-2">
              <Button onClick={() => { setShowMultiScreenGuide(false); openDetachedWindow("preview"); }} className="flex-1 bg-[#0099CC] hover:bg-[#0088B5] text-white text-xs" data-testid="guide-detach-preview">
                <ExternalLink className="w-3.5 h-3.5 mr-1" /> Detacher Preview
              </Button>
              <Button onClick={() => { setShowMultiScreenGuide(false); openDetachedWindow("program"); }} className="flex-1 bg-red-600 hover:bg-red-700 text-white text-xs" data-testid="guide-detach-program">
                <ExternalLink className="w-3.5 h-3.5 mr-1" /> Detacher Program
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* ===== PTZ CONTROLS PANEL (floating) ===== */}
      {showPtzPanel && ptzSource && (
        <div className="fixed bottom-4 right-4 z-40 w-72 shadow-2xl shadow-black/50" data-testid="ptz-panel-floating">
          <PtzControls
            source={ptzSource}
            token={token}
            onClose={() => { setShowPtzPanel(false); setPtzSource(null); }}
          />
        </div>
      )}

      {/* ===== DEVICE PANEL (modal) ===== */}
      {showDevicePanel && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm" onClick={() => setShowDevicePanel(false)} data-testid="device-panel-modal">
          <div className="w-[420px] max-h-[85vh]" onClick={e => e.stopPropagation()}>
            <DevicePanel
              devices={deviceMgr.allDevices}
              captureCards={deviceMgr.captureCards}
              audioInterfaces={deviceMgr.audioInterfaces}
              permissionGranted={deviceMgr.permissionGranted}
              lastEvent={deviceMgr.lastEvent}
              scanning={deviceMgr.scanning}
              onScan={deviceMgr.scan}
              onAddVideoSource={(device) => {
                // Add capture card or webcam as a camera source
                const name = device.brand ? `${device.brand} — ${device.label}` : device.label;
                if (session) {
                  axios.post(`${API}/api/broadcast/sources`, {
                    session_id: session.id, type: "camera", name,
                    config: { device_id: device.id, device_label: device.label, category: device.category, brand: device.brand },
                  }, { headers }).then(res => {
                    setSources(prev => [...prev, res.data]);
                    toast.success(`${device.category === "capture_card" ? "Carte de capture" : "Camera"} "${name}" ajoutee`);
                  }).catch(() => toast.error("Erreur ajout source"));
                }
              }}
              onSelectAudioInput={(device) => {
                toast.success(`Entree audio: ${device.label}`);
              }}
              onSelectAudioOutput={(device) => {
                toast.success(`Sortie audio: ${device.label}`);
              }}
              onClose={() => setShowDevicePanel(false)}
            />
          </div>
        </div>
      )}

      {/* ===== HOT-PLUG NOTIFICATION (bottom-left toast) ===== */}
      {deviceMgr.lastEvent && !showDevicePanel && (
        <div className={`fixed bottom-4 left-4 z-40 px-4 py-2.5 rounded-xl shadow-xl flex items-center gap-2.5 animate-in slide-in-from-left ${
          deviceMgr.lastEvent.type === "connected"
            ? "bg-[#10B981]/20 border border-[#10B981]/40 text-[#10B981]"
            : "bg-red-500/20 border border-red-500/40 text-red-400"
        }`} data-testid="hotplug-toast">
          <Usb className="w-4 h-4" />
          <span className="text-xs font-medium">
            {deviceMgr.lastEvent.type === "connected"
              ? `${deviceMgr.lastEvent.device?.brand || "Peripherique"} connecte`
              : "Peripherique deconnecte"
            }
          </span>
        </div>
      )}
    </div>
  );
}

// ===== CONTENT RENDERER (for Preview/Program monitors) =====
function ContentRenderer({ content, isPreview, cameraStreams = {}, sources = [] }) {
  if (!content) return <div className="w-full h-full bg-black" />;

  if (content.type === "black") return <div className="w-full h-full bg-black flex items-center justify-center"><Moon className="w-8 h-8 text-white/10" /></div>;

  if (content.type === "wait") return (
    <div className="w-full h-full flex items-center justify-center p-4" style={{ background: `linear-gradient(135deg, ${content.config?.bg_color || "#0B1120"}, ${content.config?.bg_end || "#1a1a2e"})` }}>
      <div className="text-center">
        {content.config?.logo_url && <img src={content.config.logo_url} alt="" className="h-8 mx-auto mb-3 object-contain" />}
        <div className="w-8 h-8 mx-auto border-2 border-white/20 border-t-white/60 rounded-full animate-spin mb-3" />
        <p className="text-white/70 text-xs">{content.message || content.config?.message || "En attente..."}</p>
      </div>
    </div>
  );

  if (content.type === "image" && content.config?.url) return (
    <div className="w-full h-full flex items-center justify-center bg-black">
      <img src={content.config.url} alt="" className="max-w-full max-h-full object-contain" />
    </div>
  );

  if (content.type === "pdf" && content.config?.full_url) return (
    <div className="w-full h-full flex items-center justify-center bg-white">
      <img src={content.config.full_url} alt="" className="max-w-full max-h-full object-contain" />
    </div>
  );

  if (content.type === "youtube") return (
    <div className="w-full h-full flex items-center justify-center bg-black">
      <div className="text-center">
        <Youtube className="w-10 h-10 text-red-500 mx-auto mb-2" />
        <p className="text-white text-xs">{content.name || "YouTube"}</p>
        <p className="text-[#64748B] text-[10px] mt-1">{content.config?.video_id}</p>
      </div>
    </div>
  );

  if (content.type === "video_url") return (
    <div className="w-full h-full flex items-center justify-center bg-black">
      <div className="text-center"><Film className="w-10 h-10 text-[#F59E0B] mx-auto mb-2" /><p className="text-white text-xs">{content.name || "Video"}</p></div>
    </div>
  );

  if (content.type === "text") return (
    <div className="w-full h-full flex items-center justify-center p-4" style={{ backgroundColor: content.config?.bg_color || "#0B1120" }}>
      <p className="text-center text-xs" style={{ color: content.config?.color || "#fff" }}>{content.config?.text?.substring(0, 100)}</p>
    </div>
  );

  if (content.type === "livestats") return (
    <div className="w-full h-full bg-gradient-to-br from-[#0B1120] to-[#0d1a2d] flex items-center justify-center">
      <div className="text-center"><BarChart3 className="w-10 h-10 text-[#0099CC] mx-auto mb-2" /><p className="text-white text-xs">Live Stats</p></div>
    </div>
  );

  if (content.type === "qr") return (
    <div className="w-full h-full bg-white flex items-center justify-center"><QrCode className="w-12 h-12 text-black" /></div>
  );

  if (content.type === "countdown") return <CountdownMini config={content.config} />;

  if (content.type === "local_video") return (
    <div className="w-full h-full bg-black flex items-center justify-center">
      <video
        src={`${API}${content.config?.url}`}
        className="w-full h-full object-contain"
        controls={!content.config?.autoplay}
        autoPlay={content.config?.autoplay}
        muted
        playsInline
        data-testid="local-video-preview"
      />
    </div>
  );

  if (content.type === "camera") {
    const sourceId = content.config?.source_id;
    const entry = cameraStreams[sourceId];
    return (
      <CameraVideoElement
        stream={entry?.stream}
        name={content.name}
        isPreview={isPreview}
        audioEnabled={entry?.audioEnabled && !isPreview}
      />
    );
  }

  if (content.type === "scene" && content.config) {
    return <SceneRenderer config={content.config} sources={sources} cameraStreams={cameraStreams} isPreview={isPreview} />;
  }

  if (content.type === "ip_camera" && content.config?.url) {
    return <IpCameraPlayer url={content.config.url} format={content.config.format} isPreview={isPreview} />;
  }

  return <div className="w-full h-full bg-black" />;
}



// IP Camera player - supports HLS (.m3u8) and direct video streams
function IpCameraPlayer({ url, format, isPreview, audioEnabled }) {
  const videoRef = useRef(null);
  const hlsRef = useRef(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!url || !videoRef.current) return;
    setError(null);

    const video = videoRef.current;
    const detectedFormat = format === "auto" || !format
      ? (url.toLowerCase().includes(".m3u8") ? "hls" : "direct")
      : format;

    if (detectedFormat === "hls") {
      import("hls.js").then(({ default: Hls }) => {
        if (!Hls.isSupported()) {
          // Fallback: try native HLS (Safari)
          video.src = url;
          video.play().catch(() => {});
          return;
        }
        if (hlsRef.current) hlsRef.current.destroy();

        // ===== 4K OPTIMIZATION =====
        // Preview: cap to 720p level, low buffer. Program: full quality.
        const hlsConfig = {
          enableWorker: true,
          lowLatencyMode: true,
          capLevelToPlayerSize: true, // Auto-limit quality to element size
          maxBufferLength: isPreview ? 10 : 30,
          maxMaxBufferLength: isPreview ? 20 : 60,
          startLevel: isPreview ? 0 : -1, // Preview: start lowest; Program: auto
        };

        const hls = new Hls(hlsConfig);
        hlsRef.current = hls;
        hls.loadSource(url);
        hls.attachMedia(video);
        hls.on(Hls.Events.ERROR, (_, data) => {
          if (data.fatal) setError(`Erreur HLS: ${data.details}`);
        });
        hls.on(Hls.Events.MANIFEST_PARSED, () => { video.play().catch(() => {}); });
      }).catch(() => setError("Impossible de charger hls.js"));
    } else {
      video.src = url;
      video.play().catch(() => {});
      video.onerror = () => setError("Flux video inaccessible ou format non supporte");
    }

    return () => {
      if (hlsRef.current) { hlsRef.current.destroy(); hlsRef.current = null; }
      video.src = "";
    };
  }, [url, format, isPreview]);

  if (error) {
    return (
      <div className="w-full h-full bg-black flex items-center justify-center">
        <div className="text-center px-3">
          <AlertCircle className="w-6 h-6 text-red-400 mx-auto mb-2" />
          <p className="text-red-400 text-[10px]">{error}</p>
          <p className="text-[#4A5568] text-[9px] mt-1">{url}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-full bg-black relative">
      <video ref={videoRef} autoPlay playsInline muted={isPreview} className="w-full h-full object-contain" data-testid="ip-camera-video" />
      <div className="absolute top-1 left-1 bg-[#F97316] text-black text-[7px] font-bold px-1.5 py-0.5 rounded">IP</div>
    </div>
  );
}


// Camera video element - attaches MediaStream to a <video> element
function CameraVideoElement({ stream, name, isPreview, audioEnabled }) {
  const videoRef = useRef(null);

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.srcObject = stream || null;
    }
    return () => {
      if (videoRef.current) videoRef.current.srcObject = null;
    };
  }, [stream]);

  if (!stream) {
    return (
      <div className="w-full h-full bg-black flex items-center justify-center">
        <div className="text-center">
          <Camera className="w-8 h-8 text-[#22D3EE]/30 mx-auto mb-2" />
          <p className="text-[#4A5568] text-[10px]">{name || "Camera"} — deconnectee</p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-full bg-black relative">
      <video
        ref={videoRef}
        autoPlay
        playsInline
        muted={!audioEnabled}
        className="w-full h-full object-contain"
        data-testid={isPreview ? "camera-preview-video" : "camera-program-video"}
      />
      {isPreview && (
        <div className="absolute top-1 right-1 flex items-center gap-1">
          <MicOff className="w-2.5 h-2.5 text-[#F59E0B]" />
          <span className="text-[7px] text-[#F59E0B] bg-black/60 px-1 rounded">Audio mute en Preview</span>
        </div>
      )}
    </div>
  );
}


// Mini countdown for Preview/Program monitors
function CountdownMini({ config }) {
  const [remaining, setRemaining] = useState(null);
  useEffect(() => {
    if (!config?.target_time) return;
    const tick = () => {
      const diff = Math.max(0, Math.floor((new Date(config.target_time).getTime() - Date.now()) / 1000));
      setRemaining(diff);
    };
    tick();
    const iv = setInterval(tick, 1000);
    return () => clearInterval(iv);
  }, [config?.target_time]);

  if (remaining === null) return <div className="w-full h-full bg-[#0B1120]" />;
  const done = remaining <= 0;
  const h = Math.floor(remaining / 3600);
  const m = Math.floor((remaining % 3600) / 60);
  const s = remaining % 60;

  return (
    <div className="w-full h-full bg-gradient-to-br from-[#0B1120] to-[#1a0a2e] flex items-center justify-center p-2">
      <div className="text-center">
        <Hourglass className="w-5 h-5 text-[#EC4899] mx-auto mb-1" />
        {done ? (
          <p className="text-green-400 text-xs font-bold">{config?.end_message || "C'est parti !"}</p>
        ) : (
          <>
            <p className="text-white/60 text-[9px] mb-0.5">{config?.message || ""}</p>
            <p className="text-white font-mono text-sm font-bold tabular-nums">
              {h > 0 && `${String(h).padStart(2, "0")}:`}{String(m).padStart(2, "0")}:{String(s).padStart(2, "0")}
            </p>
          </>
        )}
      </div>
    </div>
  );
}


// Scene Renderer — displays multi-source composition in PREVIEW/PROGRAM monitors
function SceneRenderer({ config, sources, cameraStreams, isPreview }) {
  if (!config?.slots || config.slots.length === 0) {
    return <div className="w-full h-full bg-black flex items-center justify-center"><span className="text-[#4A5568] text-xs">Scene vide</span></div>;
  }

  const layout = config.layout || "fullscreen";
  const activeSlots = config.slots;
  const audioSlot = config.audio_slot ?? 0;

  const renderSlotContent = (slot, index) => {
    const src = sources.find(s => s.id === slot.source_id);
    if (!src) return <div className="w-full h-full bg-[#0B1120] flex items-center justify-center"><span className="text-[9px] text-[#4A5568]">Source manquante</span></div>;

    const hasAudio = slot.has_audio && !isPreview;

    if (src.type === "camera") {
      const entry = cameraStreams[src._cameraSourceId || src.config?.source_id];
      return <CameraVideoElement stream={entry?.stream} name={src.name} isPreview={isPreview} audioEnabled={hasAudio} />;
    }
    if (src.type === "image" && src.config?.url) {
      return <img src={src.config.url} alt={src.name} className="w-full h-full object-cover" />;
    }
    if (src.type === "pdf" && src.config?.pages) {
      const page = src.config.pages[0];
      return <img src={page?.full_url || page?.thumb_url} alt="PDF" className="w-full h-full object-contain bg-black" />;
    }
    if (src.type === "text") {
      return (
        <div className="w-full h-full bg-black flex items-center justify-center p-2">
          <p className="text-white text-center" style={{ fontSize: `${Math.min(src.config?.font_size || 24, 24)}px`, color: src.config?.color || "#fff" }}>
            {src.config?.text || "Texte"}
          </p>
        </div>
      );
    }
    if (src.type === "livestats") {
      return (
        <div className="w-full h-full bg-gradient-to-br from-[#0B0F19] to-[#151B2B] flex items-center justify-center">
          <span className="text-[#0099CC] text-xs font-bold">LIVE STATS</span>
        </div>
      );
    }
    return <div className="w-full h-full bg-[#0B1120] flex items-center justify-center"><span className="text-[9px] text-[#94A3B8]">{src.name}</span></div>;
  };

  // Layout rendering
  if (layout === "fullscreen" && activeSlots[0]) {
    return <div className="w-full h-full">{renderSlotContent(activeSlots[0], 0)}</div>;
  }

  if (layout === "split-2") {
    return (
      <div className="w-full h-full grid grid-cols-2 gap-[2px] bg-black">
        {activeSlots.slice(0, 2).map((slot, i) => <div key={i} className="overflow-hidden">{renderSlotContent(slot, i)}</div>)}
      </div>
    );
  }

  if (layout === "split-3") {
    return (
      <div className="w-full h-full grid grid-cols-2 gap-[2px] bg-black">
        <div className="row-span-2 overflow-hidden">{activeSlots[0] ? renderSlotContent(activeSlots[0], 0) : null}</div>
        <div className="overflow-hidden">{activeSlots[1] ? renderSlotContent(activeSlots[1], 1) : null}</div>
        <div className="overflow-hidden">{activeSlots[2] ? renderSlotContent(activeSlots[2], 2) : null}</div>
      </div>
    );
  }

  if (layout === "grid-4") {
    return (
      <div className="w-full h-full grid grid-cols-2 grid-rows-2 gap-[2px] bg-black">
        {activeSlots.slice(0, 4).map((slot, i) => <div key={i} className="overflow-hidden">{renderSlotContent(slot, i)}</div>)}
      </div>
    );
  }

  return <div className="w-full h-full bg-black" />;
}
